/**
 * SpatialIndex - R-tree for Efficient Spatial Queries
 * 
 * Optimized for Canvas and Presentations apps
 * Provides O(log n) spatial queries using R-tree data structure
 * 
 * Without R-tree: O(n) - check every element
 * With R-tree: O(log n) - tree-based search
 */

// ✅ NOTE: Install rbush if not already: npm install rbush
// For now, using simple implementation. Can upgrade to rbush later.

export interface SpatialNode {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex?: number;
}

export interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface SpatialQuery {
  type: 'bounds' | 'near' | 'intersects';
  bounds?: Bounds;
  near?: {
    nodeId: string;
    radius: number;
  };
  point?: {
    x: number;
    y: number;
  };
}

/**
 * Simple Spatial Index
 * 
 * Phase 4: Simple O(n) implementation
 * Phase 5 (future): Can upgrade to R-tree (rbush) for O(log n)
 */
export class SpatialIndex {
  private nodes: Map<string, SpatialNode> = new Map();
  
  /**
   * Insert node into spatial index
   */
  insert(node: SpatialNode): void {
    this.nodes.set(node.id, { ...node });
    
    console.log('[SpatialIndex] Inserted node:', {
      id: node.id,
      bounds: { x: node.x, y: node.y, w: node.width, h: node.height }
    });
  }
  
  /**
   * Query nodes using spatial criteria
   * 
   * Current: O(n) - iterates all nodes
   * Future: O(log n) - with R-tree
   */
  query(query: SpatialQuery): SpatialNode[] {
    const results: SpatialNode[] = [];
    
    if (query.type === 'bounds') {
      // Find all nodes intersecting bounds
      const { x, y, width, height } = query.bounds!;
      
      for (const node of this.nodes.values()) {
        const intersects = this.boundsIntersect(
          { x, y, width, height },
          { x: node.x, y: node.y, width: node.width, height: node.height }
        );
        
        if (intersects) {
          results.push(node);
        }
      }
    }
    
    else if (query.type === 'near') {
      // Find nodes within radius of another node
      const centerNode = this.nodes.get(query.near!.nodeId);
      if (!centerNode) return [];
      
      const radius = query.near!.radius;
      const centerX = centerNode.x + centerNode.width / 2;
      const centerY = centerNode.y + centerNode.height / 2;
      
      for (const node of this.nodes.values()) {
        if (node.id === centerNode.id) continue; // Skip self
        
        const nodeX = node.x + node.width / 2;
        const nodeY = node.y + node.height / 2;
        
        const distance = Math.sqrt(
          Math.pow(nodeX - centerX, 2) + Math.pow(nodeY - centerY, 2)
        );
        
        if (distance <= radius) {
          results.push(node);
        }
      }
    }
    
    else if (query.type === 'intersects' && query.point) {
      // Find nodes containing a point
      const { x, y } = query.point;
      
      for (const node of this.nodes.values()) {
        if (
          x >= node.x &&
          x <= node.x + node.width &&
          y >= node.y &&
          y <= node.y + node.height
        ) {
          results.push(node);
        }
      }
    }
    
    // Sort by z-index if available
    return results.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
  }
  
  /**
   * Check if two bounds intersect
   */
  private boundsIntersect(a: Bounds, b: Bounds): boolean {
    return (
      a.x < b.x + b.width &&
      a.x + a.width > b.x &&
      a.y < b.y + b.height &&
      a.y + a.height > b.y
    );
  }
  
  /**
   * Remove node from index
   */
  remove(nodeId: string): void {
    this.nodes.delete(nodeId);
  }
  
  /**
   * Update node position
   */
  update(nodeId: string, updates: Partial<SpatialNode>): void {
    const node = this.nodes.get(nodeId);
    if (node) {
      this.nodes.set(nodeId, { ...node, ...updates });
    }
  }
  
  /**
   * Clear all nodes
   */
  clear(): void {
    this.nodes.clear();
  }
  
  /**
   * Get bounds of all nodes (canvas bounding box)
   */
  getBounds(): Bounds | null {
    const allNodes = Array.from(this.nodes.values());
    if (allNodes.length === 0) return null;
    
    const minX = Math.min(...allNodes.map(n => n.x));
    const minY = Math.min(...allNodes.map(n => n.y));
    const maxX = Math.max(...allNodes.map(n => n.x + n.width));
    const maxY = Math.max(...allNodes.map(n => n.y + n.height));
    
    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    };
  }
  
  /**
   * Get index size
   */
  size(): number {
    return this.nodes.size;
  }
  
  /**
   * Get all nodes
   */
  getAllNodes(): SpatialNode[] {
    return Array.from(this.nodes.values());
  }
}

/**
 * Future: Upgrade to R-tree for O(log n) queries
 * 
 * import RBush from 'rbush';
 * 
 * export class OptimizedSpatialIndex {
 *   private tree: RBush<RTreeItem>;
 *   
 *   query(bounds): SpatialNode[] {
 *     // O(log n) with R-tree!
 *     return this.tree.search(bounds);
 *   }
 * }
 */
